import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { AuthContext } from '../auth/AuthProvider';
import { managedLocations, whereLocation } from '../auth/managedLocations';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Alert,
  Button
} from '@mui/material';
import { formatDistance } from 'date-fns';
import { LoadingSpinner } from '../../shared/components';

interface ClientSuccessMetric {
  id: string;
  client_id: string;
  client_name?: string;
  coach_name?: string;
  goal: string;               
  goal_update: string;         
  goal_achieved: boolean; 
  target1: string;
  target2: string;
  target3: string;
  target1_update: string;
  target2_update: string;
  target3_update: string;
  target1_achieved: boolean;
  target2_achieved: boolean;
  target3_achieved: boolean;
  comments: string;
  created_by: string;
  created_by_uid: string;
  created_by_email: string;
  updated_at: Date | string;
  created_at: Date | string;
  primary_location?: string;
}

interface ClientData {
  id: string;
  client_id: string;
  client_name: string;
  lead_coach_id: string;
  lead_coach_name: string;
  primary_location: string;
  active_programme: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Helper function to reformat names from "Lastname, Firstname" to "Firstname Lastname"
function formatName(name: string | undefined): string {
  if (!name) return '';
  
  // Check if the name is in "Lastname, Firstname" format
  if (name.includes(',')) {
    const parts = name.split(',');
    return `${parts[1].trim()} ${parts[0].trim()}`;
  }
  
  return name;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`success-tabpanel-${index}`}
      aria-labelledby={`success-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SuccessOverview: React.FC = () => {
  const { user, userMetadata } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [clientSuccessData, setClientSuccessData] = useState<ClientSuccessMetric[]>([]);
  const [recentUpdates, setRecentUpdates] = useState<ClientSuccessMetric[]>([]);
  const [overdueUpdates, setOverdueUpdates] = useState<ClientSuccessMetric[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Fetch client success data
  useEffect(() => {
    const fetchClientSuccessData = async () => {
      if (!user || !userMetadata) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Get all clients first
        const clientsRef = collection(db, 'bigquery_cache/client_data/comprehensive_details');
        let clientsQuery;
        
        // Filter by location for managers
        const locations = managedLocations(userMetadata);
        if (userMetadata.role === 'MANAGER' && locations.length > 0) {
          clientsQuery = query(
            clientsRef,
            whereLocation('primary_location', locations),
            where('active_programme', '==', true)
          );
        } else {
          // Admin sees all active clients
          clientsQuery = query(
            clientsRef,
            where('active_programme', '==', true)
          );
        }
        
        const clientsSnapshot = await getDocs(clientsQuery);
        const clients: ClientData[] = [];
        
        clientsSnapshot.forEach((doc) => {
          const data = doc.data();
          clients.push({
            id: doc.id,
            client_id: data.client_id,
            client_name: data.client_name,
            lead_coach_id: data.lead_coach_id,
            lead_coach_name: data.lead_coach_name,
            primary_location: data.primary_location,
            active_programme: data.active_programme
          });
        });
        
        // Now get all success metrics for these clients
        const successMetrics: ClientSuccessMetric[] = [];
        const clientSuccessMap = new Map<string, ClientSuccessMetric[]>();
        
        for (const client of clients) {
          const successRef = collection(db, 'client_success');
          const successQuery = query(
            successRef,
            where('client_id', '==', client.client_id),
            orderBy('created_at', 'desc')
          );
          
          const successSnapshot = await getDocs(successQuery);
          const clientMetrics: ClientSuccessMetric[] = [];
          
          successSnapshot.forEach((doc) => {
            const data = doc.data();
            const metric: ClientSuccessMetric = {
              id: doc.id,
              client_id: data.client_id,
              client_name: client.client_name,
              coach_name: client.lead_coach_name,
              primary_location: client.primary_location,
              goal: data.goal || '',
              goal_update: data.goal_update || '',
              goal_achieved: data.goal_achieved || false,
              target1: data.target1 || '',
              target2: data.target2 || '',
              target3: data.target3 || '',
              target1_update: data.target1_update || '',
              target2_update: data.target2_update || '',
              target3_update: data.target3_update || '',
              target1_achieved: data.target1_achieved || false,
              target2_achieved: data.target2_achieved || false,
              target3_achieved: data.target3_achieved || false,
              comments: data.comments || '',
              created_by: data.created_by || '',
              created_by_uid: data.created_by_uid || '',
              created_by_email: data.created_by_email || '',
              updated_at: data.updated_at?.toDate() || new Date(),
              created_at: data.created_at?.toDate() || new Date()
            };
            
            clientMetrics.push(metric);
            successMetrics.push(metric);
          });
          
          if (clientMetrics.length > 0) {
            clientSuccessMap.set(client.client_id, clientMetrics);
          }
        }
        
        // Process and categorize the data
        setClientSuccessData(successMetrics);
        
        // Get recent updates (latest 2 per client)
        const recent: ClientSuccessMetric[] = [];
        clientSuccessMap.forEach((metrics, clientId) => {
          // Take up to 2 most recent updates
          const recentClientMetrics = metrics.slice(0, 2);
          recent.push(...recentClientMetrics);
        });
        
        // Sort by most recent first
        recent.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB.getTime() - dateA.getTime();
        });
        
        setRecentUpdates(recent);
        
        // Get overdue updates (clients with latest update > 6 weeks ago)
        const sixWeeksAgo = new Date();
        sixWeeksAgo.setDate(sixWeeksAgo.getDate() - 42); // 6 weeks = 42 days
        
        const overdue: ClientSuccessMetric[] = [];
        clientSuccessMap.forEach((metrics, clientId) => {
          if (metrics.length > 0) {
            const latestUpdate = metrics[0]; // First is most recent due to orderBy
            const updateDate = new Date(latestUpdate.created_at);
            
            if (updateDate < sixWeeksAgo) {
              overdue.push(latestUpdate);
            }
          }
        });
        
        // Sort overdue by oldest first
        overdue.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateA.getTime() - dateB.getTime();
        });
        
        setOverdueUpdates(overdue);
        
      } catch (err) {
        console.error('Error fetching client success data:', err);
        setError('Failed to load client success data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchClientSuccessData();
  }, [user, userMetadata]);
  
  // Navigate to client details
  const handleRowClick = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };
  
  // Render success update summary
  const renderSuccessUpdateSummary = (metric: ClientSuccessMetric) => {
    const details = [];
    
    if (metric.goal) {
      details.push(`Goal: ${metric.goal}`);
      if (metric.goal_update) {
        details.push(`Update: ${metric.goal_update}`);
      }
    }
    
    if (metric.target1) {
      details.push(`Target 1: ${metric.target1}`);
      if (metric.target1_update) {
        details.push(`Update: ${metric.target1_update}`);
      }
    }
    
    if (metric.target2) {
      details.push(`Target 2: ${metric.target2}`);
      if (metric.target2_update) {
        details.push(`Update: ${metric.target2_update}`);
      }
    }
    
    if (metric.target3) {
      details.push(`Target 3: ${metric.target3}`);
      if (metric.target3_update) {
        details.push(`Update: ${metric.target3_update}`);
      }
    }
    
    if (metric.comments) {
      details.push(`Comments: ${metric.comments}`);
    }
    
    return details.join(' | ');
  };
  
  // Format date for display
  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString();
  };
  
  // Get time ago string
  const getTimeAgo = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return formatDistance(dateObj, new Date(), { addSuffix: true });
  };
  
  if (!user || !userMetadata || (userMetadata.role !== 'ADMIN' && userMetadata.role !== 'MANAGER')) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to access this page.
        </Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Client Success Overview
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, width: '100%', overflow: 'auto' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="success overview tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ 
            minHeight: 48,
            '& .MuiTabs-flexContainer': {
              justifyContent: { xs: 'flex-start', md: 'flex-start' }
            },
            '& .MuiTab-root': {
              minHeight: 48,
              minWidth: { xs: 'auto', sm: 160 },
              px: { xs: 2, sm: 3 }
            }
          }}
        >
          <Tab label="Recent Updates" />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Overdue Updates
                {overdueUpdates.length > 0 && (
                  <Chip 
                    label={overdueUpdates.length} 
                    color="error" 
                    size="small" 
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            } 
          />
        </Tabs>
      </Box>
      
      {isLoading ? (
        <LoadingSpinner minHeight="300px" />
      ) : (
        <>
          {/* Recent Updates Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>
              Recent Client Success Updates
            </Typography>
            
            {recentUpdates.length === 0 ? (
              <Alert severity="info">
                No recent client success updates found.
              </Alert>
            ) : (
              <TableContainer component={Paper} elevation={2}>
                <Table aria-label="recent client success updates">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client Name</TableCell>
                      <TableCell>Coach</TableCell>
                      <TableCell>Update Date</TableCell>
                      <TableCell>Success Update Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentUpdates.map((metric) => (
                      <TableRow 
                        key={metric.id}
                        hover
                        onClick={() => handleRowClick(metric.client_id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body1">
                            {formatName(metric.client_name)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatName(metric.coach_name)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(metric.created_at)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getTimeAgo(metric.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {renderSuccessUpdateSummary(metric)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
          
          {/* Overdue Updates Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Overdue Client Success Updates
              </Typography>
              <Typography variant="subtitle2" color="error">
                {overdueUpdates.length} clients overdue (no updates in 6+ weeks)
              </Typography>
            </Box>
            
            {overdueUpdates.length === 0 ? (
              <Alert severity="success">
                No overdue client success updates. All clients have been updated within the last 6 weeks.
              </Alert>
            ) : (
              <TableContainer component={Paper} elevation={2}>
                <Table aria-label="overdue client success updates">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client Name</TableCell>
                      <TableCell>Coach</TableCell>
                      <TableCell>Last Update</TableCell>
                      <TableCell>Success Update Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overdueUpdates.map((metric) => (
                      <TableRow 
                        key={metric.id}
                        hover
                        onClick={() => handleRowClick(metric.client_id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body1">
                            {formatName(metric.client_name)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatName(metric.coach_name)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(metric.created_at)}
                          </Typography>
                          <Typography variant="caption" color="error">
                            {getTimeAgo(metric.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {renderSuccessUpdateSummary(metric)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </>
      )}
    </Container>
  );
};

export default SuccessOverview;
